import {useContext, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import MemberContext from '../../../MemberContext';
import UserStateContext from '../../../UserStateContext';
import useExperiment from '../../../../hooks/useExperiment';
import useShopPersonalizedRecommendationsFlag from '../../../../hooks/useShopPersonalizedRecommendationsFlag';
import useUnleashFlagsSettled from '../../../../hooks/useUnleashFlagsSettled';
import getOverrideParams from '../../../../utils/parseUrlParams';
import {
  EXPERIMENT_CONFIGS,
  EXPERIMENT_SHOP_PERSONALIZED_RECOMMENDATIONS,
} from '../../../../constants/experiments';

const SHOP_PATH = '/shop';

const useShopPersonalizedRecommendationsExperiment = () => {
  const {pathname} = useLocation();
  const [hasQualified, setHasQualified] = useState(false);
  const {
    member: {id: memberId},
  } = useContext(MemberContext);
  const {userStateData, isFetching: isFetchingUserData = true} = useContext(
    UserStateContext,
  );

  const experimentName = EXPERIMENT_SHOP_PERSONALIZED_RECOMMENDATIONS;

  const config = EXPERIMENT_CONFIGS[experimentName]?.[process.env.ENV];
  const {experimentId, variants} = config ?? {};

  const controlVariantId = variants?.control?.id;
  const variant1Id = variants?.variant1?.id;
  const variant2Id = variants?.variant2?.id;

  const isOnShop = pathname === SHOP_PATH;

  // The app renders before the onboarding redirect lands, so hold out members it will send there.
  const hasUserVisitedRegistry =
    !isFetchingUserData &&
    (userStateData?.hasVisitedRegistry ||
      typeof userStateData?.registryFirstVisitDate === 'string');

  const meetsBucketingCriteria = isOnShop && hasUserVisitedRegistry;
  const isEligible = hasQualified || meetsBucketingCriteria;

  const isFeatureFlagEnabled = useShopPersonalizedRecommendationsFlag();
  const areFlagsSettled = useUnleashFlagsSettled();

  const override = getOverrideParams(experimentId);
  const hasOverride = typeof override === 'string';

  // !!experimentId keeps flipper inert in environments without a configured id
  const shouldUseExperiment =
    !hasOverride && isFeatureFlagEnabled && isEligible && !!experimentId;

  const {
    isLoading,
    isActive,
    variant: hookVariant,
    isInControl: hookIsInControl,
  } = useExperiment({
    name: experimentName,
    isEnabled: shouldUseExperiment,
  });

  // Leaving Shop must not disable the experiment and drop an assignment made there.
  useEffect(() => {
    if (meetsBucketingCriteria && hookVariant && !hasQualified) {
      setHasQualified(true);
    }
  }, [meetsBucketingCriteria, hookVariant, hasQualified]);

  const resolvedVariant = hasOverride ? override : hookVariant;

  const isExperimentLoading = useMemo(() => {
    if (hasOverride) return false;
    if (!experimentId) return false;
    if (!areFlagsSettled) return true;
    if (!isFeatureFlagEnabled) return false;
    if (!isEligible && !isOnShop) return true;
    if (!isEligible) return isFetchingUserData;
    // Flipper reports loading:false until its own effect starts the assignment.
    if (memberId && !resolvedVariant && isActive) return true;

    return isLoading;
  }, [
    hasOverride,
    experimentId,
    areFlagsSettled,
    isFeatureFlagEnabled,
    isEligible,
    isOnShop,
    isFetchingUserData,
    memberId,
    resolvedVariant,
    isActive,
    isLoading,
  ]);

  const matchesOverride = variantId =>
    override.toString().trim() === variantId.toString().trim();

  const isAssignedTo = variantId => {
    if (!variantId) return false;
    if (hasOverride) return matchesOverride(variantId);
    if (!isActive) return false;

    return resolvedVariant === variantId;
  };

  const isInControl = (() => {
    if (!controlVariantId) return false;
    if (hasOverride) return matchesOverride(controlVariantId);
    if (!isActive) return true;

    return !!hookIsInControl;
  })();
  const isInVariant1 = isAssignedTo(variant1Id);
  const isInVariant2 = isAssignedTo(variant2Id);

  return useMemo(
    () => ({
      experimentId,
      variant: resolvedVariant,
      isInControl,
      isInVariant1,
      isInVariant2,
      isExperimentLoading,
      isFeatureFlagEnabled,
    }),
    [
      experimentId,
      resolvedVariant,
      isInControl,
      isInVariant1,
      isInVariant2,
      isExperimentLoading,
      isFeatureFlagEnabled,
    ],
  );
};

export default useShopPersonalizedRecommendationsExperiment;
